/**
 * O passo de CARREGAR o arquivo anexado pelo chat, e a frase que entrega
 * o resultado ao agente.
 *
 * Mesmo desenho do clipe do Eka: **os bytes NUNCA passam pelo agente**. O
 * arquivo sobe por HTTP puro (multipart, via BFF), e o que chega ao
 * modelo é só o `id` e os metadados. Isso não é economia de token — é o
 * que impede um contrato inteiro de virar contexto de LLM.
 *
 * A frase devolvida é uma MENSAGEM DO USUÁRIO. O agente reage a ela: sabe
 * o que foi anexado, onde ficou, e se dá para ler o conteúdo. Sem essa
 * última parte ele responderia sobre um documento que não pode abrir.
 */
import { dealDocumentsApi } from '../../modules/pipeline/services/dealDocumentsApi';
import { mktApi } from '../../modules/mkt/api/mktApi';
import type { DestinoDoAnexo } from './destinoDoAnexo';

/** Só os estados em que o agente CONSEGUE responder sobre o conteúdo. */
function podeLer(status: string | undefined): boolean {
  return status === 'pronta';
}

export async function anexarPeloChat(
  arquivo: File,
  destino: DestinoDoAnexo,
): Promise<string> {
  if (destino.tipo === 'deal' && destino.dealId) {
    const doc = await dealDocumentsApi.anexar(destino.dealId, arquivo);

    const leitura = podeLer(doc.extracao?.status)
      ? 'O texto dele foi extraído — você pode responder sobre o conteúdo ' +
        `usando o id ${doc.id}.`
      : // Dizer que dá para ler quando não dá faria o agente inventar
        // resposta sobre um documento que ele não abriu. O motivo do
        // backend vai junto, porque ele já explica em pt-BR.
        'NÃO foi possível extrair o texto dele' +
        (doc.extracao?.motivo ? ` (${doc.extracao.motivo})` : '') +
        '. Não responda sobre o conteúdo deste arquivo — diga que ele foi ' +
        'guardado mas você não consegue lê-lo.';

    return (
      `Anexei o arquivo "${doc.nome_do_arquivo}" aos documentos do negócio ` +
      `${destino.dealId}. Ele ficou registrado como "${doc.tipo}". ${leitura} ` +
      'Confirme o que foi guardado e pergunte se quero renomear ou ' +
      'reclassificar.'
    );
  }

  // Marketing: o MESMO caminho que o clipe do Eka já usava — o arquivo
  // entra no acervo como rascunho e o questionário completa os metadados.
  const criado = await mktApi.createContent({
    title: arquivo.name,
    type: 'other',
  });
  await mktApi.uploadContentFile(criado.id, arquivo);
  return (
    `Anexei um criativo para o acervo de marketing: "${arquivo.name}". ` +
    `Ele foi carregado como rascunho — content_id=${criado.id}. ` +
    'Rode o questionário de upload para completar os metadados (tipo de ' +
    'conteúdo, formato de criativo, segmento, campanha e descrição).'
  );
}
